/**
 * Dashboard Page
 * 
 * Main dashboard with aggregated metrics across all businesses
 */

import React from 'react'
import { Row, Col, Card, Statistic, Table, Spin, Alert, Typography } from 'antd'
import {
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  ShopOutlined,
} from '@ant-design/icons'
import { Column } from '@ant-design/charts'
import { useGetDashboardQuery } from '../services/corporate'

const { Title } = Typography

const Dashboard: React.FC = () => {
  const { data, isLoading, error } = useGetDashboardQuery()

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading dashboard...</div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <Alert
        message="Error Loading Dashboard"
        description="Failed to load dashboard data. Please try again later."
        type="error"
        showIcon
      />
    )
  }

  const { summary, businesses } = data

  // Prepare chart data
  const revenueChartData = businesses.map((b) => ({
    business: b.business.name,
    revenue: b.metrics.revenue,
  }))

  const jobsChartData = businesses.map((b) => ({
    business: b.business.name,
    jobs: b.metrics.jobs,
  }))

  // Table columns
  const columns = [
    {
      title: 'Business Name',
      dataIndex: ['business', 'name'],
      key: 'name',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Revenue',
      dataIndex: ['metrics', 'revenue'],
      key: 'revenue',
      render: (value: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value),
      sorter: (a: any, b: any) => a.metrics.revenue - b.metrics.revenue,
    },
    {
      title: 'Jobs',
      dataIndex: ['metrics', 'jobs'],
      key: 'jobs',
      sorter: (a: any, b: any) => a.metrics.jobs - b.metrics.jobs,
    },
    {
      title: 'Customers',
      dataIndex: ['metrics', 'customers'],
      key: 'customers',
      sorter: (a: any, b: any) => a.metrics.customers - b.metrics.customers,
    },
    {
      title: 'Invoices',
      dataIndex: ['metrics', 'invoices'],
      key: 'invoices',
      sorter: (a: any, b: any) => a.metrics.invoices - b.metrics.invoices,
    },
  ]

  return (
    <div>
      <Title level={2}>Corporate Dashboard</Title>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={summary.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Jobs"
              value={summary.totalJobs}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Customers"
              value={summary.totalCustomers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Businesses"
              value={summary.businessCount}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Revenue Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Revenue by Business" bordered={false}>
            <Column
              data={revenueChartData}
              xField="business"
              yField="revenue"
              label={{
                position: 'top',
                formatter: (datum: any) =>
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    notation: 'compact',
                  }).format(datum.revenue),
              }}
              xAxis={{
                label: {
                  autoRotate: true,
                  autoHide: false,
                },
              }}
              meta={{
                revenue: {
                  alias: 'Revenue',
                  formatter: (v: number) =>
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(v),
                },
              }}
            />
          </Card>
        </Col>

        {/* Jobs Chart */}
        <Col xs={24} lg={12}>
          <Card title="Jobs by Business" bordered={false}>
            <Column
              data={jobsChartData}
              xField="business"
              yField="jobs"
              label={{
                position: 'top',
              }}
              xAxis={{
                label: {
                  autoRotate: true,
                  autoHide: false,
                },
              }}
              meta={{
                jobs: {
                  alias: 'Jobs',
                },
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Business Performance Table */}
      <Card title="Business Performance Details" bordered={false}>
        <Table
          columns={columns}
          dataSource={businesses}
          rowKey={(record) => record.business.id}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} businesses`,
          }}
        />
      </Card>
    </div>
  )
}

export default Dashboard

